import { AbstractComponent } from "appworks/components/abstract-component";
import { Layers } from "appworks/graphics/layers/layers";
import { ParticleService } from "appworks/graphics/particles/particle-service";
import { Container } from "appworks/graphics/pixi/container";
import { ParticleContainer } from "appworks/graphics/pixi/particle-container";
import { RenderOrientation } from "appworks/graphics/pixi/render-orientation";
import { SpineContainer } from "appworks/graphics/pixi/spine-container";
import { SpineService } from "appworks/graphics/spine/spine-service";
import { gameState } from "appworks/model/game-state";
import { CurrencyService } from "appworks/services/currency/currency-service";
import { Services } from "appworks/services/services";
import { SoundService } from "appworks/services/sound/sound-service";
import { fadeIn2, fadeOut2 } from "appworks/utils/animation/fade2";
import { tweenNumberText } from "appworks/utils/animation/tween-number-text";
import { Contract } from "appworks/utils/contracts/contract";
import { Parallel } from "appworks/utils/contracts/parallel";
import { Sequence } from "appworks/utils/contracts/sequence";
import { TweenContract } from "appworks/utils/contracts/tween-contract";
import { RandomFromArray, RandomRangeInt } from "appworks/utils/math/random";
import { Timer } from "appworks/utils/timer";
import { Easing } from "appworks/utils/tween";
import { Tween } from "appworks/utils/tween";
import { gameLayers } from "game-layers";
import { EggPickSchema, FowlPickBonusResult } from "model/results/fowl-pick-bonus-result";
import { Signal } from "signals";
import { SlingoLadderResult } from "slingo/model/results/slingo-ladder-result";

const animations = {
    idle1: "idle_1",
    idle2: "idle_2",
    idle3: "idle_3",
    idle4: "idle_4",
    idle5: "idle_5",
    idle6: "idle_6",
    headTilt: "idle_head_tilt",
    headTurn: "idle_head_turn",
    tired: "idle_tired",
    dropLeft: "idle_after_egg_drop_left",
    dropRight: "idle_after_egg_drop_right",
    flyAwayLeft: "fly_away_left",
    flyAwayRight: "fly_away_right",
    flyLoopLeft: "fly_loop_left",
    flyLoopRight: "fly_loop_right",
    flyLandLeft: "fly_landing_left",
    flyLandRight: "fly_landing_right",
    lookLeft: "eyes_look_left",
    lookRight: "eyes_look_right",
    blink: "eyes_blink",
    laying: "laying_anim",
    shuffle: "shuffle_anim",
} as const;

const eggAnimations = {
    static: {
        white: "white_egg_in",
        silver: "silver_egg_in",
        gold: "gold_egg_in",
        diamond: "diamond_egg_in",
        fox: "fox_egg_in",
    },
    landing: {
        white: "white_egg_landing",
        silver: "silver_egg_landing",
        gold: "gold_egg_landing",
        diamond: "diamond_egg_landing",
        fox: "fox_egg_landing",
    },
} as const;

type bonusScenes = keyof typeof gameLayers.EggPickBonus.scene;

export class FowlEggPickBonusComponent extends AbstractComponent {
    public gameEnd: Signal;

    protected layer: typeof gameLayers.EggPickBonus;

    protected uiLayer: typeof gameLayers.BonusUI;

    protected paytableLayer: typeof gameLayers.EggPickPaytable;

    protected currentScene: bonusScenes;

    protected pickAnims: SpineContainer[];

    protected hits: EggPickSchema[];
    protected misses: EggPickSchema[];
    protected jackpot?: EggPickSchema;

    protected totalWin: number;

    protected particleEmitter?: ParticleContainer;

    public constructor() {
        super();
        this.layer = gameLayers.EggPickBonus;
        this.uiLayer = gameLayers.BonusUI;
        this.paytableLayer = gameLayers.EggPickPaytable;
        this.currentScene = "default";
        this.pickAnims = [];
        this.gameEnd = new Signal();
        this.hits = [];
        this.misses = [];
        this.totalWin = 0;
        // make sure egg prize value text is hidden
        this.layer.onSceneEnter.add((nextScene: bonusScenes) => {
            if (nextScene !== "single_tier" && nextScene !== "three_tier") {
                return;
            }
            const text = this.layer.scene.active.contents.prize_value;
            text.alpha = 0;
            // NOTE: we have separate 'perch' assets for portrait/landscape
            // we want to wrap these in containers so it makes the rest of our logic more straightforward.
            // the default ordering will be:
            // * perch_bgPrt
            // * perch_fgPrt
            // * perch_bg_lds
            // * perch_fg_lds
            // we want to map this to:
            // * container
            //      * perch_bgPrt
            //      * perch_bg_lds
            // * container
            //      * perch_fgPrt
            //      * perch_fd_lds
            const wrapperBg = new Container();
            const wrapperFg = new Container();
            wrapperBg.children.push(this.layer.container.children.shift());
            wrapperFg.children.push(this.layer.container.children.shift());
            wrapperBg.children.push(this.layer.container.children.shift());
            wrapperFg.children.push(this.layer.container.children.shift());
            this.layer.add(wrapperFg, undefined, true);
            this.layer.add(wrapperBg, undefined, true);
            // particles
            this.particleEmitter = new ParticleContainer();
            this.layer.add(this.particleEmitter, undefined, true);
            this.layer.container.setChildIndex(this.particleEmitter, 2);
            Services.get(ParticleService).add("feathers", this.particleEmitter);
            this.particleEmitter.emitter.emit = false;
        });
        // setup the UI on enter
        this.uiLayer.onSceneEnter.add((nextScene: keyof typeof gameLayers.BonusUI.scene) => {
            if (nextScene !== "eggpick") {
                return;
            }
            const { pickMeter_value, totalWinMeter_value } = this.uiLayer.scene.active.contents;
            // NOTE: assetworks doesn't export the lineHeight property on text
            pickMeter_value.text = `${this.hits.length}/${this.hits.length}`;
            totalWinMeter_value.text = Services.get(CurrencyService).format(this.totalWin);
        });
    }

    public get prize(): number {
        return this.totalWin;
    }

    public enter(result: FowlPickBonusResult, transitionDuration: number, delay: number = 0): Contract<void> {
        // get correct scene
        const scene = result.bonusVariant;
        this.currentScene = scene;
        // set picks
        this.hits = [...result.hits];
        this.misses = [...result.misses];
        this.jackpot = result.jackpotEgg;
        // Figure out which ladder level we are on
        const ladderResult = gameState.getCurrentGame().getLatestResultOfType(SlingoLadderResult);
        let paytableScene: keyof typeof gameLayers.EggPickPaytable.scene;
        switch (ladderResult.total) {
            case 4:
                paytableScene = "paytable_1";
                break;
            case 5:
                paytableScene = "paytable_2";
                break;
            case 7:
                paytableScene = "paytable_3";
                break;
            case 9:
                paytableScene = "paytable_4";
                break;
            case 12:
                paytableScene = "paytable_5";
                break;
            default:
                paytableScene = "paytable_0";
                break;
        }
        // Set to 0 if scatter triggered
        if (result.bonusVariant === "single_tier") {
            paytableScene = "paytable_0";
        }

        // transition tweens
        const createFadeTween = (...layer: Layers[]) =>
            layer.map((l: Layers) => {
                const t = new Tween(l.container);
                l.container.alpha = 0;
                t.to({ alpha: 1 }, transitionDuration);
                t.delay(delay);
                return () => TweenContract.wrapTween(t);
            });

        // update pick meter
        return new Sequence([
            // setup spines
            () => Contract.wrap(() => this.initPicks(result.chickenPositions)),
            // animate picks falling in
            () => this.revealPicks(),
            () => Contract.wrap(() => Services.get(SoundService).customEvent("chickens_idle")),
            // show ui
            () => this.uiLayer.setScene("eggpick"),
            () => this.paytableLayer.setScene(paytableScene),
            () => new Parallel(createFadeTween(this.uiLayer)),
        ]);
    }

    public exit(): Contract<void> {
        this.reset();
        return new Parallel([() => this.uiLayer.defaultScene(), () => this.paytableLayer.defaultScene()]);
    }

    public enablePicks(): void {
        this.pickAnims.forEach(pick => {
            // set buttonmode
            pick.interactive = true;
            pick.buttonMode = true;
            // TODO: set button hit areas
            pick.addListener("pointerup", ev => this.pickClick(ev));
        });
    }

    protected reset(): void {
        this.pickAnims = [];
        this.hits = [];
        this.misses = [];
        this.jackpot = undefined;
        this.totalWin = 0;
        this.currentScene = "default";
        this.particleEmitter.destroy({ children: true });
        this.particleEmitter = undefined;
    }

    protected initPicks(selectors: number[]): void {
        if (this.currentScene === "default") {
            throw new Error("Invalid call to start pick idle animations");
        }
        const scene = this.layer.scene[this.currentScene].contents;
        const validKeys = selectors.map(x => `pick${x + 1}`);
        for (const key in scene) {
            if (!(scene[key] instanceof SpineContainer)) {
                continue;
            }
            const anim = scene[key] as SpineContainer;
            if (validKeys.indexOf(key) === -1) {
                // NOTE: remove invalid pick from the stage
                this.layer.remove(anim);
                anim.destroy({ children: true });
                continue;
            }
            anim.spines.landscape.stateData.defaultMix = 200;
            anim.spines.portrait.stateData.defaultMix = 200;
            this.pickAnims.push(anim);
        }
    }

    protected revealPicks(): Contract<void> {
        const duration = 5000;
        /**
         * Animates the chickens falling from above the stage into positions. The path is basically a sine wave.
         * Oscillate by simultaneously tweening the x and y position of the container.
         */
        const getTweenContract = (pick: SpineContainer): Contract<void> => {
            // grab the initial position
            const x0Lnd = pick.landscape.x;
            const x0Prt = pick.portrait.x;
            const y0Lnd = pick.landscape.y;
            const y0Prt = pick.portrait.y;
            const sign = RandomFromArray([-1, 1]);
            const startYLnd = gameLayers.EggPickBonus.scene.active.contents.positions["pick_above"].landscape.y - 300;
            const startYPrt = gameLayers.EggPickBonus.scene.active.contents.positions["pick_above"].portrait.y - 300;
            pick.landscape.y = startYLnd;
            pick.portrait.y = startYPrt;
            // x tween is an oscillator
            // x(t) := x0 + Asin(2 * PI * t * f)
            const tween = new Tween();
            tween.to({}, duration);
            const A = 300;
            const oscillations = 2;
            const F = 2 * Math.PI * oscillations;
            tween.onUpdate(t => {
                const dx = sign * A * Math.sin(F * t);
                const x1Lnd = x0Lnd + dx;
                const x1Prt = x0Prt + dx;
                // NOTE: both orientations will oscillate in the same direction
                // therefore it is sufficient to compare it in one direction
                if (x1Lnd < pick.landscape.x) {
                    pick.play(animations.flyLoopRight);
                } else if (x1Lnd > pick.landscape.x) {
                    pick.play(animations.flyLoopLeft);
                }
                // translate
                pick.landscape.x = x1Lnd;
                pick.portrait.x = x1Prt;
                pick.landscape.y = (y0Lnd - startYLnd) * t + startYLnd;
                pick.portrait.y = (y0Prt - startYPrt) * t + startYPrt;
            });
            // Random delay to break up the reveals
            tween.delay(RandomRangeInt(0, 1000));
            return new Sequence([
                // first animate the chickens falling into position
                () => TweenContract.wrapTween(tween),
                // then have them land
                () =>
                    pick.playOnce(
                        pick.getAnimations()[pick.getAnimationIndex()].name === animations.flyLoopLeft ? animations.flyLandLeft : animations.flyLandRight
                    ),
                // finally start idling
                () => Contract.wrap(() => this.idlePick(pick)),
            ]);
        };
        // tween all picks
        return new Parallel(this.pickAnims.map(pick => () => getTweenContract(pick)));
    }

    protected idlePick(pick: SpineContainer): void {
        // play idle
        const animation = RandomFromArray([animations.idle1, animations.idle2, animations.idle3, animations.idle4, animations.idle5, animations.idle6]);
        pick.play(animation);
    }

    protected pickClick(ev: PIXI.InteractionEvent): void {
        const target = ev.currentTarget as SpineContainer;
        const others = this.pickAnims.filter(x => x !== target);
        // disable all layer interaction
        this.layer.container.interactiveChildren = false;
        // disable target interaction
        target.interactive = false;
        // clean up
        target.removeAllListeners();
        let egg = this.hits.shift();
        this.totalWin += egg.value;
        new Sequence([
            // hide pick label
            () => fadeOut2(this.uiLayer.scene.active.contents.bonus_label, 100),
            // egg drops
            // laying scene
            () => {
                Services.get(SoundService).customEvent("chicken_pick");
                // NOTE: we wrap instead of using playOnce because we want the animation to loop.
                // The timeout contract here is to ensure a minimum duration for the laying, which
                // builds up anticipation.
                target.play(animations.laying);
                others.forEach(pick =>
                    pick.play(target[RenderOrientation.orientation].x < pick[RenderOrientation.orientation].x ? animations.lookLeft : animations.lookRight)
                );
                // play particles
                if (this.particleEmitter) {
                    this.particleEmitter.dualPosition.setPosition(target.dualPosition);
                    const bounds = target.getBounds();
                    this.particleEmitter.dualPosition.landscape.x += bounds.width / 2;
                    this.particleEmitter.dualPosition.landscape.y += bounds.height / 2;
                    this.particleEmitter.dualPosition.portrait.x += bounds.width / 2;
                    this.particleEmitter.dualPosition.portrait.y += bounds.height / 2;
                    this.particleEmitter.emitter.emit = true;
                }
                // Delay before reveal to allow the sound to play
                return Contract.getTimeoutContract(2500);
            },
            // FP-45: extra fox egg drop
            () => {
                if (this.hits.length !== 0 || this.jackpot === undefined) {
                    return Contract.empty();
                }
                const lastEgg = egg;
                egg = this.jackpot;
                // Update pick text
                Timer.setTimeout(() => {
                    // change pick meter text
                    const total = this.uiLayer.scene.eggpick.contents.pickMeter_value.text.split("/")[1];
                    this.uiLayer.scene.eggpick.contents.pickMeter_value.text = `${this.hits.length}/${total}`;
                }, 500);
                // Reveal last egg before revealing the fox egg
                // SFP-51
                return new Sequence([
                    () => new Parallel([
                        () => this.revealPrize(target, lastEgg, true),
                        () => Contract.getDelayedContract(500, () => {
                            // stop feathers
                            if (this.particleEmitter) {
                                this.particleEmitter.emitter.emit = false;
                            }
                            target.play(animations.tired);
                            return Contract.getTimeoutContract(500);
                        })
                    ]),
                    () => {
                        target.play(animations.laying);
                        if (this.particleEmitter) {
                            this.particleEmitter.emitter.emit = true;
                        }
                        return Contract.getTimeoutContract(1000);
                    }
                ])
            },
            () =>
                new Parallel([
                    // reveal the prize!
                    () => this.revealPrize(target, egg, true),
                    () =>
                        Contract.getDelayedContract(500, () => {
                            // stop feathers
                            if (this.particleEmitter) {
                                this.particleEmitter.emitter.emit = false;
                            }
                            // FP-14 play tired animations three times then have the picks exit scene
                            new Sequence([
                                () => target.playOnce(animations.tired),
                                () => target.playOnce(animations.tired),
                                () => target.playOnce(animations.tired),
                            ]).then(() => {
                                this.pickExitStage(target);
                            });
                            // change pick meter text
                            // NOTE: if this is a fox egg, the text already reads '0/X' so this won't do anything
                            const total = this.uiLayer.scene.eggpick.contents.pickMeter_value.text.split("/")[1];
                            this.uiLayer.scene.eggpick.contents.pickMeter_value.text = `${this.hits.length}/${total}`;
                            return Contract.getTimeoutContract(500);
                        }),
                ]),
            // reset others
            () => {
                others.forEach(pick => {
                    const animation = RandomFromArray([
                        animations.idle1,
                        animations.idle2,
                        animations.idle3,
                        animations.idle4,
                        animations.idle5,
                        animations.idle6,
                    ]);
                    if (pick.interactive) {
                        pick.play(animation);
                    }
                });
                return Contract.empty();
            },
            // flow padding
            () => Contract.getTimeoutContract(this.hits.length === 0 && this.jackpot !== undefined ? 2000 : 1000),
        ]).then(() => {
            // reset interaction
            this.layer.container.interactiveChildren = true;
            // once we have exhausted all available picks, trigger the signal
            if (this.hits.length === 0) {
                this.endCurrentGame();
            } else {
                fadeIn2(this.uiLayer.scene.active.contents.bonus_label, 100).execute();
                Services.get(SoundService).customEvent("chickens_idle");
            }
        });
    }

    protected endCurrentGame(): void {
        const missingPicks = this.pickAnims.filter(pick => pick.interactive);
        missingPicks.forEach(pick => {
            // disable and clean up all the pick spines
            pick.interactive = false;
            pick.removeAllListeners();
            // remaining chickens start to lay
            pick.play(animations.laying);
        });
        new Parallel(
            missingPicks.map(pick => {
                return () => {
                    const egg = this.misses.shift();
                    const { prize_value } = this.layer.scene.active.contents;
                    // Create a new prize text object
                    prize_value.text = egg.value > 0 ? Services.get(CurrencyService).format(egg.value) : "";
                    const prize = prize_value.clone();
                    // Add to stage above the perch
                    this.layer.container.addChildAt(prize, this.layer.container.getChildIndex(pick));
                    prize.alpha = 0;
                    // set the position
                    prize.landscape.setPosition(pick.dualPosition.landscape);
                    prize.portrait.setPosition(pick.dualPosition.portrait);

                    prize.landscape.y += 50;
                    prize.portrait.y += 50;

                    // apply tinting and scale (for misses)
                    const tint = 0xbdbcbc;
                    const scale = 0.75;
                    prize.landscape.scale.set(scale);
                    prize.portrait.scale.set(scale);
                    prize.tint = tint;

                    // Exit scene
                    this.pickExitStage(pick, true);
                    // Fade in egg and prize
                    fadeIn2(prize, 300).execute();
                    // Fade out
                    return Contract.getDelayedContract(1500, () => fadeOut2(prize, 300));
                };
            })
        )
            // signal back to state
            .then(() => this.gameEnd.dispatch());
    }

    /**
     * Animates the prize value reveal.
     * First the egg is dropped from the chicken, then once it lands the picked prize value is revealed above the egg.
     * If the reveal is a 'hit' then the total win meter is updated. Finally we fade out the egg and the prize to 'reset' for the next reveal.
     */
    protected revealPrize(pick: SpineContainer, egg: EggPickSchema, isHit: boolean): Contract<void> {
        // get tween target positions
        const idx = pick.name.charAt(pick.name.length - 1);
        const fromPosition = this.layer.scene[this.currentScene].contents.positions[`egg_upper_${idx}`];
        const toPosition = this.layer.scene[this.currentScene].contents.positions[`egg_lower_${idx}`];

        // setup spine container
        const spine = Services.get(SpineService).createSpineContainer("pick_egg_animation", fromPosition);
        if (egg.color === "fox") {
            spine.name = "fox_egg";
        }
        this.layer.add(spine, undefined, true);
        this.layer.container.setChildIndex(spine, 1);

        // tween both orientations
        // rate in distance units/ms
        const speed = 1;
        const duration = Math.min(toPosition.landscape.y - fromPosition.landscape.y, toPosition.portrait.y - fromPosition.portrait.y) / speed;
        const tLnd = new Tween(spine.landscape).to({ x: toPosition.landscape.x, y: toPosition.landscape.y }, duration).easing(Easing.Quadratic.In);
        const tPrt = new Tween(spine.portrait).to({ x: toPosition.portrait.x, y: toPosition.portrait.y }, duration).easing(Easing.Quadratic.In);

        const { prize_value } = this.layer.scene.active.contents;
        // set the value
        prize_value.text = egg.value > 0 ? Services.get(CurrencyService).format(egg.value) : "";
        // set the position
        prize_value.landscape.setPosition(toPosition.landscape);
        prize_value.portrait.setPosition(toPosition.portrait);
        // NOTE: whenever the prize_value updates (on each render) slotworks will offset the position of the prize_value.
        // It assumes that all prize_value objects are positioned based on the top left corner
        prize_value.landscape.x -= prize_value.landscape.width / 2;
        prize_value.portrait.x -= prize_value.portrait.width / 2;
        prize_value.landscape.y -= prize_value.landscape.height / 2;
        prize_value.portrait.y -= prize_value.portrait.height / 2;
        // NOTE: so far we have aligned the text to the top left corner of the spine container
        // in order to center it above the egg, we get the actual size of the spine and offset by half the width
        const spinePositions = spine.getSpinePosition();
        prize_value.landscape.x += spinePositions.landscape.width / 2;
        prize_value.portrait.x += spinePositions.portrait.width / 2;
        // we also need shift the container up slightly so its guaranteed not to overlap with the spine
        prize_value.landscape.y -= prize_value.landscape.height / 2;
        prize_value.portrait.y -= prize_value.portrait.height / 2;

        // apply tinting and scale (for misses)
        const tint = isHit ? 0xffffff : 0xbdbcbc;
        const scale = isHit ? 1 : 0.75;
        prize_value.landscape.scale.set(scale);
        prize_value.portrait.scale.set(scale);
        prize_value.tint = tint;
        spine.spines.landscape.tint = tint;
        spine.spines.portrait.tint = tint;

        return new Sequence([
            // tween down the egg
            () => Contract.wrap(() => Services.get(SoundService).customEvent("lay_egg")),
            () => spine.playOnce(eggAnimations.static[egg.color]),
            () => new Parallel([() => TweenContract.wrapCancellableTween(tLnd), () => TweenContract.wrapCancellableTween(tPrt)]),
            // egg lands
            () => Contract.wrap(() => Services.get(SoundService).customEvent("egg_land")),
            () => spine.playOnce(eggAnimations.landing[egg.color]), // reveal the prize
            () =>
                new Parallel([
                    // reveal the prize value
                    () => fadeIn2(prize_value, 300),
                    // update prize meter (optional)
                    () => {
                        if (!isHit || egg.color === "fox") {
                            return Contract.empty();
                        }
                        Services.get(SoundService).customEvent("pick_prize_reveal");
                        return TweenContract.wrapTween(
                            tweenNumberText({
                                text: this.uiLayer.scene.eggpick.contents.totalWinMeter_value,
                                from: this.totalWin - egg.value,
                                to: this.totalWin,
                                duration: 1000,
                                mutator: (value: number) => Services.get(CurrencyService).format(value),
                            })
                        );
                    },
                ]),
            // artificial delay
            () => Contract.getTimeoutContract(200),
            // remove text and egg
            () => {
                if (egg.color === "fox") {
                    return fadeOut2(prize_value, 1);
                }
                return fadeOut2([prize_value, spine], 300);
            },
        ]);
    }

    /**
     * Animate pick exiting the stage
     **/
    protected pickExitStage(pick: SpineContainer, isMiss: boolean = false): void {
        const sign = RandomFromArray([-1, 1]);
        const duration = 3000;

        // Move to front
        pick.parent.setChildIndex(pick, pick.parent.children.length - 1);

        pick.spines.landscape.stateData.defaultMix = 0.5;
        pick.spines.portrait.stateData.defaultMix = 0.5;

        // Play away animation
        pick.playOnce(sign === 1 ? animations.flyAwayLeft : animations.flyAwayRight)
            // Then fly anim
            .then(() => pick.play(sign === 1 ? animations.flyLoopLeft : animations.flyLoopRight));

        const Easing = (t: number): number => {
            return 5 * (t - 0.125) ** 2 - 0.0781;
        };
        // X tweens
        new Tween(pick.landscape).to({ x: pick.landscape.x + sign * 600 }, duration).start();
        new Tween(pick.portrait).to({ x: pick.portrait.x + sign * 600 }, duration).start();
        // Y Tweens
        new Tween(pick.landscape)
            .to({ y: gameLayers.EggPickBonus.scene.active.contents.positions[isMiss ? "pick_above" : "pick_below"].landscape.y }, duration)
            .easing(Easing)
            .start();
        new Tween(pick.portrait)
            .to({ y: gameLayers.EggPickBonus.scene.active.contents.positions[isMiss ? "pick_above" : "pick_below"].portrait.y }, duration)
            .easing(Easing)
            .start()
            .onStart(() => {
                Services.get(SoundService).customEvent("chicken_fly_away");
            })
            .onComplete(() => {
                // Stop rendering after
                pick.stop(true);
                pick.renderable = false;
            });
    }
}
